import type { BrandView } from "@kopi/contracts";
import { MAX_IMAGE_EDGE_LABEL, MAX_IMAGE_UPLOAD_BYTES, MAX_IMAGE_UPLOAD_LABEL } from "@kopi/contracts/client";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@kopi/ui/components/card";
import { ConfirmButton } from "@kopi/ui/components/confirm-button";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { toast } from "@kopi/ui/components/sonner";
import { DEFAULT_BRAND_NAME } from "@kopi/ui/lib/brand";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ChangeEvent, type FormEvent, useId, useRef, useState } from "react";
import { TenantLogo } from "@/components/brand";
import { Page, PageHeader } from "@/components/page-header";
import { settings } from "@/i18n/messages/settings";
import { type AreaTranslate, useMessages } from "@/i18n/provider";
import { canTenantWide } from "@/lib/access";
import { ApiRequestError, api, unwrap } from "@/lib/api";
import { brandQuery, meScopesQuery } from "@/lib/queries";

export const Route = createFileRoute("/_authed/settings/brand")({
  staticData: {
    section: "preferences",
    parent: { label: "nav.settings", to: "/settings" },
    crumb: "page.brand",
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(brandQuery),
  component: BrandSettingsPage,
});

/** Messages for brand writes (the API's English `message` is never shown). */
function brandError(t: AreaTranslate<typeof settings.id>, error: unknown): string {
  if (!(error instanceof ApiRequestError)) return t("common.saveOffline");
  switch (error.code) {
    case "forbidden":
      return t("settings.ownerOnly");
    case "validation_failed":
      return t("settings.brandNameInvalid");
    case "image_too_large":
      return t("settings.logoTooLarge", { max: MAX_IMAGE_UPLOAD_LABEL });
    case "unsupported_image":
      return t("settings.unsupportedImage");
    case "image_dimensions_too_large":
      return t("settings.logoDimensions", { max: MAX_IMAGE_EDGE_LABEL });
    case "image_too_detailed":
      return t("settings.logoTooDetailed");
    default:
      return t("common.saveFailed");
  }
}

function BrandSettingsPage() {
  const { t } = useMessages(settings);
  const { data: brand } = useSuspenseQuery(brandQuery);
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const editable = canTenantWide(scopes, "outlets.manage");

  return (
    <Page width="narrow">
      <PageHeader title={t("page.brand")} subtitle={t("settings.brandSubtitle")} />
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.preview")}</CardTitle>
          <CardDescription>{t("settings.previewHint")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-6">
          <div className="rounded-xl border bg-sidebar px-5 py-4">
            <TenantLogo brand={brand} size={40} />
          </div>
          <TenantLogo brand={brand} variant="mark" size={64} />
        </CardContent>
      </Card>
      {editable ? (
        <>
          <NameCard key={brand.name} brand={brand} />
          <LogoCard brand={brand} />
        </>
      ) : (
        <p className="text-muted-foreground text-sm">{t("settings.ownerOnly")}</p>
      )}
    </Page>
  );
}

function useStoreBrand() {
  const queryClient = useQueryClient();
  return (brand: BrandView) => queryClient.setQueryData(brandQuery.queryKey, brand);
}

function NameCard(props: { brand: BrandView }) {
  const { t } = useMessages(settings);
  const store = useStoreBrand();
  const [name, setName] = useState(props.brand.isDefaultName ? "" : props.brand.name);
  const nameId = useId();
  const errorId = useId();

  const save = useMutation({
    mutationFn: async (next: string | null) => unwrap(await api.v1.brand.patch({ name: next })),
    onSuccess: (updated) => {
      store(updated);
      toast.success(t(updated.isDefaultName ? "settings.nameReset" : "settings.nameSaved"));
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate(name.trim());
  }

  const trimmed = name.trim();
  const unchanged = props.brand.isDefaultName ? trimmed === "" : trimmed === props.brand.name;

  return (
    <Card>
      <form onSubmit={submit} noValidate className="flex flex-col gap-6">
        <CardHeader>
          <CardTitle>{t("settings.brandName")}</CardTitle>
          <CardDescription>{t("settings.brandNameHint", { name: DEFAULT_BRAND_NAME })}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-1.5">
          <Label htmlFor={nameId}>{t("common.name")}</Label>
          <Input
            id={nameId}
            value={name}
            maxLength={60}
            placeholder={DEFAULT_BRAND_NAME}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={save.isError}
            {...(save.isError && { "aria-describedby": errorId })}
          />
          {save.error && (
            <p id={errorId} role="alert" className="text-destructive text-sm">
              {brandError(t, save.error)}
            </p>
          )}
        </CardContent>
        <CardFooter className="flex-wrap gap-2">
          <Button type="submit" disabled={save.isPending || unchanged || trimmed === ""}>
            {t(save.isPending ? "common.saving" : "settings.saveName")}
          </Button>
          {!props.brand.isDefaultName && (
            <Button type="button" variant="ghost" disabled={save.isPending} onClick={() => save.mutate(null)}>
              {t("settings.useDefaultName")}
            </Button>
          )}
        </CardFooter>
      </form>
    </Card>
  );
}

const ACCEPTED_LOGO_TYPES = "image/jpeg,image/png,image/webp";

/** The logo: the server validates it, pads it to a square (nothing is cropped) and re-encodes it; this only uploads. */
function LogoCard(props: { brand: BrandView }) {
  const { t } = useMessages(settings);
  const store = useStoreBrand();
  const fileInput = useRef<HTMLInputElement>(null);
  const [tooLarge, setTooLarge] = useState(false);

  const upload = useMutation({
    mutationFn: async (file: File) => unwrap(await api.v1.brand.logo.put({ file })),
    onSuccess: (updated) => {
      store(updated);
      toast.success(t("settings.logoSaved"));
    },
  });
  const remove = useMutation({
    mutationFn: async () => unwrap(await api.v1.brand.logo.delete()),
    onSuccess: (updated) => {
      store(updated);
      toast.success(t("settings.logoRemoved"));
    },
  });

  function pick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const oversized = file.size > MAX_IMAGE_UPLOAD_BYTES;
    setTooLarge(oversized);
    remove.reset();
    if (!oversized) upload.mutate(file);
  }

  const hasLogo = props.brand.logoUrl !== null;
  const busy = upload.isPending || remove.isPending;
  const error = upload.error ?? remove.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {t("settings.logo")} {!hasLogo && <Badge variant="outline">{t("settings.default")}</Badge>}
        </CardTitle>
        <CardDescription>{t("settings.logoHint", { max: MAX_IMAGE_UPLOAD_LABEL })}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Input
          ref={fileInput}
          type="file"
          accept={ACCEPTED_LOGO_TYPES}
          className="hidden"
          tabIndex={-1}
          aria-hidden
          onChange={pick}
        />
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" disabled={busy} onClick={() => fileInput.current?.click()}>
            {t(
              upload.isPending
                ? "settings.uploading"
                : hasLogo
                  ? "settings.replaceLogo"
                  : "settings.uploadLogo",
            )}
          </Button>
          {hasLogo && (
            <ConfirmButton
              variant="ghost"
              title={t("settings.removeLogoTitle")}
              description={t("settings.removeLogoHint")}
              confirmLabel={t("settings.removeLogo")}
              disabled={busy}
              onConfirm={() => remove.mutate()}
            >
              {t("settings.removeLogo")}
            </ConfirmButton>
          )}
        </div>
        {tooLarge && (
          <p role="alert" className="text-destructive text-sm">
            {t("settings.logoTooLarge", { max: MAX_IMAGE_UPLOAD_LABEL })}
          </p>
        )}
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {brandError(t, error)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
